import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAnalytics } from '../context/AnalyticsContext';
import { useGetOrganizerCamps, useGetOrganizer, useToggleCampEnabled, useDeleteCampDate } from '../api/organizerAdmin';
import { Camp } from '../utils/types';
import { Calendar, Clock, Users, DollarSign, AlertCircle, Edit, Power, Camera, ExternalLink, Plus, Trash2 } from 'lucide-react';
import CampEditModal from '../components/admin/CampEditModal';
import CampImageUploadModal from '../components/admin/CampImageUploadModal';
import OrganizerInfoEditModal from '../components/admin/OrganizerInfoEditModal';
import CampDateModal from '../components/admin/CampDateModal';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import { CampFormatUtils } from '../utils/campFormatUtils';
import './OrganizerAdmin.css';

const OrganizerAdmin: React.FC = () => {
  const { organizerId } = useParams<{ organizerId: string }>();
  const { trackPageView } = useAnalytics();

  // Fetch organizer's camps
  const {
    data: campsData,
    isLoading: isLoadingCamps,
    error: campsError,
  } = useGetOrganizerCamps(organizerId);

  const { data: organizerData } = useGetOrganizer(organizerId);

  const toggleCampEnabled = useToggleCampEnabled(organizerId || '');
  const deleteCampDate = useDeleteCampDate(organizerId || '');
  const [editingCamp, setEditingCamp] = useState<Camp | null>(null);
  const [imageUploadCampId, setImageUploadCampId] = useState<string | null>(null);
  const [editingOrganizer, setEditingOrganizer] = useState(false);
  const [addingDateForCampId, setAddingDateForCampId] = useState<string | null>(null);
  const [deletingDateIds, setDeletingDateIds] = useState<Set<string>>(new Set());

  const handleDeleteCampDate = async (campId: string, dateId: string) => {
    setDeletingDateIds((prev) => new Set(prev).add(dateId));
    try {
      await deleteCampDate.mutateAsync({ campId, dateId });
    } finally {
      setDeletingDateIds((prev) => { const next = new Set(prev); next.delete(dateId); return next; });
    }
  };

  // Track page view
  useEffect(() => {
    if (organizerId) {
      trackPageView(`/admin/organizer/${organizerId}`, 'Organizer Admin');
    }
  }, [organizerId, trackPageView]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getCampImage = (camp: Camp): string => {
    return camp.images.find(image => image.type === 'default')?.url || '/images/camp-card-default-image.png';
  };

  const handleToggleEnabled = async (camp: Camp) => {
    try {
      await toggleCampEnabled.mutateAsync({
        campId: camp.id,
        status: camp.status && camp.status === 'unpublished' ? 'published' : 'unpublished',
      });
    } catch (error) {
      console.error('Failed to toggle camp enabled state:', error);
    }
  };


  // Loading state
  if (isLoadingCamps) {
    return (
      <div className="app">
        <Header />
        <main className="main-content">
          <div className="admin-loading">
            <div className="loading-spinner"></div>
            <p>Loading admin dashboard...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Error state
  if (campsError) {
    return (
      <div className="app">
        <Header />
        <main className="main-content">
          <div className="admin-error">
            <AlertCircle className="icon icon--xl" />
            <h2>Error Loading Dashboard</h2>
            <p>Unable to load the admin dashboard. Please try again.</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // No camps state
  if (!campsData?.camps || campsData.camps.length === 0) {
    return (
      <div className="app">
        <Header />
        <main className="main-content">
          <div className="admin-empty">
            <h2>No Camps Found</h2>
            <p>You don't have any camps to manage yet.</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="app">
      <Header />

      <main className="main-content">
        <div className="admin-container">
          {/* Admin Header */}
          <div className="admin-header">
            <h1 className="admin-title">Admin Dashboard</h1>
          </div>

          {/* Organizer Info */}
          {organizerData && (
            <div className="admin-organizer-card">
              {organizerData.images?.find((img) => img.type === 'default') && (
                <div className="admin-organizer-logo-wrap">
                  <img
                    src={organizerData.images.find((img) => img.type === 'default')!.url}
                    alt={organizerData.name}
                    className="admin-organizer-logo"
                  />
                </div>
              )}
              <div className="admin-organizer-info">
                <h2 className="admin-organizer-name">{organizerData.name}</h2>
                {organizerData.description && (
                  <div className="admin-organizer-description">
                    <ReactMarkdown rehypePlugins={[rehypeSanitize]}>
                      {organizerData.description}
                    </ReactMarkdown>
                  </div>
                )}
                <button
                  className="btn btn--secondary btn--sm"
                  onClick={() => setEditingOrganizer(true)}
                >
                  <Edit className="icon icon--xs" />
                  Edit Info
                </button>
              </div>
            </div>
          )}

          {/* Camp Listing */}
          <div className="admin-camps-list">
            <h2 className="admin-subtitle">Manage your camps</h2>
            {campsData.camps.map((camp) => {
              const priceParts = CampFormatUtils.getPriceParts(camp);
              const isEnabled = camp.status !== 'unpublished';

              return (
                <div key={camp.id} className={`admin-camp-row ${!isEnabled ? 'admin-camp-row--disabled' : ''}`}>
                  <div className="admin-camp-image-container">
                    <img
                      src={getCampImage(camp)}
                      alt={camp.name}
                      className="admin-camp-image"
                    />
                    {!isEnabled && (
                      <div className="admin-camp-disabled-overlay">
                        <span>Disabled</span>
                      </div>
                    )}
                  </div>

                  <div className="admin-camp-details">
                    <h3 className="admin-camp-name">
                      {camp.name}
                      {isEnabled && (
                        <Link to={`/camp/${camp.id}`} className="admin-camp-view-link" target="_blank" rel="noopener noreferrer">
                          View camp <ExternalLink className="icon icon--xs" />
                        </Link>
                      )}
                    </h3>

                    {camp.description && (
                      <p className="admin-camp-description">
                        {camp.description.length > 150
                          ? `${camp.description.substring(0, 150).replace(/\s+\S*$/, '')}...`
                          : camp.description}
                      </p>
                    )}

                    <div className="admin-camp-info">
                      <div className="admin-info-item">
                        <Clock className="icon icon--sm" />
                        <span>{CampFormatUtils.formatTimeRange(camp.start_time, camp.end_time)}</span>
                      </div>

                      <div className="admin-info-item">
                        <Users className="icon icon--sm" />
                        <span>{CampFormatUtils.formatAgeRange(camp.min_age, camp.max_age)}</span>
                      </div>
                    </div>

                    {/* Camp Dates List */}
                    {camp.camp_dates.length > 0 ? (
                      <div className="admin-camp-dates-list">
                        {[...camp.camp_dates].sort((a, b) => a.start_date.localeCompare(b.start_date)).map((campDate) => (
                          <div key={campDate.id} className="admin-camp-date-row">
                            <div className="admin-camp-date-info">
                              <span className="admin-camp-date-range">
                                <Calendar className="icon icon--xs" />
                                {CampFormatUtils.formatDateRange(campDate.start_date, campDate.end_date, true)}
                              </span>
                              <span className="admin-camp-date-capacity">
                                <Users className="icon icon--xs" />
                                {campDate.current_capacity} / {campDate.max_capacity || '∞'}
                              </span>
                            </div>
                            <div className="admin-camp-date-actions">
                              <Link
                                to={`/admin/organizer/${organizerId}/camps/${camp.id}/dates/${campDate.id}`}
                                className="admin-camp-date-link"
                              >
                                <Edit className="icon icon--xs" />
                                View Bookings
                              </Link>
                              <button
                                className="btn btn--danger btn--sm"
                                onClick={() => handleDeleteCampDate(camp.id, campDate.id)}
                                disabled={deletingDateIds.has(campDate.id)}
                              >
                                <Trash2 className="icon icon--xs" />
                                {deletingDateIds.has(campDate.id) ? 'Deleting...' : 'Delete'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="admin-camp-no-dates">No dates scheduled</div>
                    )}
                  </div>

                  <div className="admin-camp-actions">
                    <div className="admin-camp-price">
                      <DollarSign className="icon icon--sm" />
                      <span className="price-amount">${priceParts.price}</span>
                      {priceParts.timeframe && (
                        <span className="price-timeframe">/ {priceParts.timeframe}</span>
                      )}
                    </div>

                    <div className="admin-buttons">
                      <button
                        className="btn btn--success btn--sm"
                        onClick={() => setAddingDateForCampId(camp.id)}
                      >
                        <Plus className="icon icon--xs" />
                        Add Date
                      </button>
                      <button
                        className="btn btn--primary btn--sm"
                        onClick={() => setEditingCamp(camp)}
                      >
                        <Edit className="icon icon--xs" />
                        Edit
                      </button>
                      <button
                        className="btn btn--secondary btn--sm"
                        onClick={() => setImageUploadCampId(camp.id)}
                      >
                        <Camera className="icon icon--xs" />
                        Edit Image
                      </button>
                      <button
                        className={`btn btn--sm ${isEnabled ? 'btn--warning' : 'btn--success'}`}
                        onClick={() => handleToggleEnabled(camp)}
                        disabled={toggleCampEnabled.isPending}
                      >
                        <Power className="icon icon--xs" />
                        {isEnabled ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      {editingOrganizer && organizerData && (
        <OrganizerInfoEditModal
          organizer={organizerData}
          isOpen={true}
          onClose={() => setEditingOrganizer(false)}
        />
      )}

{editingCamp && organizerId && (
        <CampEditModal
          camp={editingCamp}
          isOpen={true}
          onClose={() => setEditingCamp(null)}
          organizerId={organizerId}
        />
      )}

      {imageUploadCampId && organizerId && campsData?.camps.find(c => c.id === imageUploadCampId) && (
        <CampImageUploadModal
          camp={campsData.camps.find(c => c.id === imageUploadCampId)!}
          isOpen={true}
          onClose={() => setImageUploadCampId(null)}
          organizerId={organizerId}
        />
      )}

      {addingDateForCampId && organizerId && campsData?.camps.find(c => c.id === addingDateForCampId) && (
        <CampDateModal
          camp={campsData.camps.find(c => c.id === addingDateForCampId)!}
          organizerId={organizerId}
          onClose={() => setAddingDateForCampId(null)}
        />
      )}

      <Footer />
    </div>
  );
};

export default OrganizerAdmin;
